import { debounce } from '@utils/debounce';

/**
 * Inicializace zobrazení a skrytí zápasů v sekcích ligy
 */
export const initTeamMatchesToggle = (wrapper: HTMLElement): void => {
  if (!wrapper) {
    return;
  }

  // Všechna tlačítka menu
  const chipButtons: NodeListOf<HTMLElement> = wrapper.querySelectorAll('[data-chip-menu-item-id]');
  // Všechny bloky s ligami
  const leagueBlocks: NodeListOf<HTMLElement> = wrapper.querySelectorAll(
    '[data-league-team-realoder]'
  );
  // Všechna tlačítka pro zobrazení/skrytí zápasů
  const toggleMatchesButtons: NodeListOf<HTMLButtonElement> = wrapper.querySelectorAll(
    '[data-view-team-matches-toggle-button]'
  );
  // Všechny kontejnery se zápasy
  const matchesContainers: NodeListOf<HTMLElement> = wrapper.querySelectorAll(
    '[data-matches-container]'
  );

  // Funkce pro získání počtu zápasů, které se mají zobrazit
  function getDefaultVisibleMatches(container: HTMLElement): number {
    // Hodnota z data atributu nebo výchozí hodnota 5
    return parseInt(container.getAttribute('data-default-visible-matches') || '5', 10);
  }

  // Funkce pro výpočet celkové výšky pro první počet zápasů
  function calculateTotalHeight(container: HTMLElement): number {
    if (!container) return 0;

    const defaultVisibleMatches = getDefaultVisibleMatches(container);
    const matches: NodeListOf<HTMLElement> = container.querySelectorAll('.match-overview-list');
    if (matches.length === 0) return 0;

    // Výpočet - součet výšek prvních N zápasů
    let totalHeight = 0;
    for (let i = 0; i < Math.min(defaultVisibleMatches, matches.length); i++) {
      totalHeight += matches[i].offsetHeight;
    }

    return totalHeight;
  }

  // Funkce pro nastavení stylů kontejneru pro zápasy
  function setupMatchesContainer(container: HTMLElement): void {
    if (!container) return;

    // Nastaví overflow: hidden a transition pro plynulé změny výšky
    container.style.overflow = 'hidden';
    container.style.transition = 'height 0.3s ease-in-out';

    // Nastaví počáteční výšku pro zobrazení prvních N zápasů
    const height = calculateTotalHeight(container);
    if (height > 0) {
      container.style.height = height + 'px';
    }

    // Nastaví data-expanded atribut na false
    container.setAttribute('data-expanded', 'false');
  }

  // Nastavení počátečních stylů pro všechny kontejnery se zápasy
  matchesContainers.forEach((container) => {
    setupMatchesContainer(container);
  });

  // Funkce pro zobrazení konkrétní ligy a aktivování tlačítka
  function showLeague(leagueId: string): void {
    // Skryje všetky ligy
    leagueBlocks.forEach((block) => {
      block.style.display = 'none';
    });

    // Zruší aktivní třídu na všech tlačítkách
    chipButtons.forEach((button) => {
      button.classList.remove('sportmod-tabs-chip-menu__chip-active');
    });

    // Zobrazí vybranou ligu
    const selectedLeagueBlock: HTMLElement | null = wrapper.querySelector(
      `[data-league-team-realoder="${leagueId}"]`
    );
    if (selectedLeagueBlock) {
      selectedLeagueBlock.style.display = 'flex';

      // Resetuje zobrazení zápasů v této lize na počáteční stav
      const leagueToggleButtons = wrapper.querySelectorAll(
        `[data-view-team-matches-toggle-button][data-league-id="${leagueId}"]`
      );
      leagueToggleButtons.forEach((button) => {
        if (button instanceof HTMLElement) {
          resetMatchesDisplay(button);
        }
      });
    }

    // Aktivuje příslušné tlačítko
    const selectedButton = wrapper.querySelector(`[data-chip-menu-item-id="${leagueId}"]`);
    if (selectedButton) {
      selectedButton.classList.add('sportmod-tabs-chip-menu__chip-active');
    }
  }

  // Funkce pro zobrazení/skrytí zápasů
  function toggleMatches(button: HTMLButtonElement): void {
    const competitionId = button.getAttribute('data-view-team-matches-toggle-button');
    const matchesContainer = wrapper.querySelector(
      `[data-team-list="${competitionId}"]`
    ) as HTMLElement | null;

    if (matchesContainer) {
      const isExpanded = matchesContainer.getAttribute('data-expanded') === 'true';

      if (isExpanded) {
        // Pokud je blok zápasů rozbalený, sbalíme ho
        matchesContainer.setAttribute('data-expanded', 'false');
        const height = calculateTotalHeight(matchesContainer);
        matchesContainer.style.height = height + 'px';
        button.textContent = button.getAttribute('data-text-expand') || 'Zobrazit všechny zapasy';
      } else {
        // Pokud je blok zápasů sbalený, rozbalíme ho
        matchesContainer.setAttribute('data-expanded', 'true');
        matchesContainer.style.height = 'auto';
        const fullHeight = matchesContainer.offsetHeight;
        // Předchozí výška před animací
        const currentHeight = calculateTotalHeight(matchesContainer);
        matchesContainer.style.height = currentHeight + 'px';

        // Použití setTimeout s 0ms zajistí, že se provede v následujícím cyklu kvůli animaci
        setTimeout(() => {
          matchesContainer.style.height = fullHeight + 'px';
        }, 0);
        button.textContent = button.getAttribute('data-text-collapse') || 'Skrýt';
      }
    }
  }

  // Funkce pro reset zobrazení zápasů na počáteční stav
  function resetMatchesDisplay(button: HTMLElement): void {
    const competitionId = button.getAttribute('data-view-team-matches-toggle-button');
    const matchesContainer = wrapper.querySelector(
      `[data-team-list="${competitionId}"]`
    ) as HTMLElement | null;

    if (matchesContainer) {
      matchesContainer.setAttribute('data-expanded', 'false');
      const height = calculateTotalHeight(matchesContainer);
      matchesContainer.style.height = height + 'px';
      button.textContent = button.getAttribute('data-text-expand') || 'Zobrazit všechny zapasy';
    }
  }

  // Zobrazení/skrytí zápasů
  toggleMatchesButtons.forEach((button) => {
    button.addEventListener('click', function () {
      toggleMatches(this);
    });
  });

  // Všechna tlačítka menu
  chipButtons.forEach((button) => {
    button.addEventListener('click', function () {
      const leagueId = this.getAttribute('data-chip-menu-item-id');
      if (leagueId) {
        showLeague(leagueId);
      }
    });
  });

  // Zobrazí první ligu na začátku (pokud existuje)
  if (chipButtons.length > 0 && leagueBlocks.length > 0) {
    const firstLeagueId = chipButtons[0].getAttribute('data-chip-menu-item-id');
    if (firstLeagueId) {
      showLeague(firstLeagueId);
    }
  }

  // Reagování na změnu velikosti okna - přepočet výšky kontejnerů
  const handleResize = (): void => {
    matchesContainers.forEach((container) => {
      if (container.getAttribute('data-expanded') === 'false') {
        const height = calculateTotalHeight(container);
        container.style.height = height + 'px';
      } else {
        // Pro rozbalené kontejnery musíme změřit celou výšku
        container.style.height = 'auto';
        const fullHeight = container.offsetHeight;
        container.style.height = fullHeight + 'px';
      }
    });
  };

  // Zpoždění pro efektivnější volání při změně velikosti
  window.addEventListener('resize', debounce(handleResize, 250));
};
